import { HttpStatusCode } from '@angular/common/http';
import {
    Component,
    EventEmitter,
    Input,
    Output,
    ViewChild
} from '@angular/core';
import {
    Options,
    TapestrySelectComponent,
    TapestryNotificationService
} from '@predictiveindex/tapestry';
import {
    ModuleUserAccessLevel,
    UserAccessSoftwareModule
} from '../../../constants-types';
import {
    ModuleName,
    ModuleUserInviteRequest,
    ModuleUserInviteResponse
} from '../../../core-data-model';
import { CompaniesDataService, ModuleUserApi } from '../../../core-services';
import { UserTypeaheadComponent } from '../../../core-ui';
import { InitDataService } from '../../../system-services';
import { TranslationService } from '../../../translations';
import { ModuleUserManagementService } from '../service/module-user-management.service';

@Component({
    selector: 'pi-module-user-management-invitation',
    styleUrls: ['module-user-management-invitation.component.less'],
    templateUrl: './module-user-management-invitation.component.html',
    standalone: false
})
export class ModuleUserManagementInvitationComponent {
    @Input() moduleName: ModuleName;
    @Output() inviteUsersEvent = new EventEmitter();

    @ViewChild('piUserTypeaheadInvitation')
    piUserTypeahead: UserTypeaheadComponent;
    @ViewChild('piSelectRole')
    piSelect: TapestrySelectComponent;

    buttonMessage: string;
    validationMessage: string;
    piSelectOptions: Options[] = [
        {
            value: ModuleUserAccessLevel[ModuleUserAccessLevel.Limited],
            displayValue: this.TR.key('module-user.accessLevel.limited'),
            isDisabled: false
        },
        {
            value: ModuleUserAccessLevel[ModuleUserAccessLevel.Full],
            displayValue: this.TR.key('module-user.accessLevel.full'),
            isDisabled: false
        },
        {
            value: ModuleUserAccessLevel[ModuleUserAccessLevel.Admin],
            displayValue: this.TR.key('module-user.accessLevel.admin'),
            isDisabled: false
        }
    ];
    piSelectDefault = this.TR.key('module-user.accessLevel.limited');
    isLoading = false;

    constructor(
        private readonly tapestryNotification: TapestryNotificationService,
        private readonly moduleUserApi: ModuleUserApi,
        public readonly sharedService: ModuleUserManagementService,
        private readonly companyService: CompaniesDataService,
        private readonly initDataService: InitDataService,
        public readonly TR: TranslationService
    ) {
        this.sharedService.moduleInvitation = this;
        this.buttonMessage = TR.key('userManagement.sendInvitations-button');
    }

    onInviteButtonClick() {
        // validations here
        if (!this.invitationValidations()) {
            // validation failed. send out validationMessage?
            throw new Error(this.validationMessage);
        }
        // invite - http post here
        if (!this.inviteUsersApi()) {
            // retrieving promise failed. toast out a message?
            throw new Error('invite failed');
        }
    }

    isReadyToInvite() {
        return (
            !!this.piUserTypeahead &&
            this.piUserTypeahead.selectedUsers.length > 0 &&
            !!this.piSelect.selected &&
            !this.piUserTypeahead.chipList.errorState
        );
    }

    getExistingUsers() {
        return this.sharedService.getExistingUsers();
    }

    invitationValidations(): boolean {
        if (this.piUserTypeahead.validation()) {
            // errorState is true
            this.validationMessage =
                this.piUserTypeahead.errorStateMessages.join(',');
            return false;
        } else if (!this.piSelect.selected) {
            this.validationMessage = this.TR.key(
                'module-userManagement.validation.noSelection'
            );
            return false;
        } else if (
            // bypass --noImplicitAny
            ModuleUserAccessLevel[this.piSelect.selected] == null
        ) {
            this.validationMessage = this.TR.key(
                'module-userManagement.validation.accessLevel.noMatch'
            );
            return false;
        }
        return true;
    }

    inviteUsersApi(): boolean {
        // lock the screen / button here, send lock event
        this.isLoading = true;
        //    invite - http post to useraccess api here to invite
        const invitations: ModuleUserInviteRequest = {
            usersToInvite: this.piUserTypeahead.selectedUsers.map((x) => ({
                userId: x.id,
                email: x.email
            })),
            softwareModule:
                UserAccessSoftwareModule[
                    this.moduleName as keyof typeof UserAccessSoftwareModule
                ],
            moduleAccessLevel:
                ModuleUserAccessLevel[
                    this.piSelect.selected as keyof typeof ModuleUserAccessLevel
                ],
            companyName: this.companyService.currentCompanyName,
            senderFirstName: this.initDataService.profile.firstName,
            senderLastName: this.initDataService.profile.lastName
        };

        this.moduleUserApi
            .inviteUserAccess(invitations)
            // .then() -> send out refresh grid event - emit event to parent
            .then(([responseBody, httpResponse]) => {
                this.inviteUsersEvent.emit();
                if (httpResponse.status == HttpStatusCode.Ok) {
                    this.notifyApiResponse(responseBody);
                    return Promise.resolve;
                } else {
                    return Promise.reject(new Error(httpResponse.statusText));
                }
            })
            // .catch() -> toast out validation message
            .catch((reason) => {
                throw new Error(reason);
            })
            // .finally() -> unlock button here, send unlock event
            .finally(() => {
                this.isLoading = false;
            });
        return true;
    }

    // response from moduleUserApi is a tuple of Success list and Error list.
    notifyApiResponse(response: ModuleUserInviteResponse) {
        const success = response.success;
        const errors = response.errors;
        // remove successful chips from input
        success.forEach((s) => this.piUserTypeahead.remove(s.email));
        if (errors.length > 0) {
            // add to error list
            this.piUserTypeahead.setError('Invite has run into some errors:');
            errors.forEach((e) => {
                this.piUserTypeahead.addError(e.email + ' ' + e.message);
            });
            this.piUserTypeahead.setInvalidChips(errors.map((x) => x.email));
            this.tapestryNotification.create({
                message: this.TR.key(
                    'userManagement.invitationSent.notification-failure'
                ),
                duration: 3000,
                type: 'error'
            });
        } else {
            // success message
            this.tapestryNotification.create({
                message: this.TR.key(
                    'userManagement.invitationSent.notification-success'
                ),
                duration: 3000,
                type: 'success'
            });
        }
    }
}
